import { Components } from '@/api/generated/api';
import { useRouterHelper } from '@/composables/useRouterHelper';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '../usePermissions';
import { useCurrentTenant } from '../schoolAdmin/useCurrentTenant';
import { useSchoolClasses } from '../useSchoolClasses';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useWhoAmI } from '../useWhoAmI';
import { buildRequestClassMailto } from '@/utils/mailto.utils';
import { useOwnerActivationChannel } from './useOwnerActivationFlow';

export interface IFirstEntrySuccessConfig {
  showTeamInvite: boolean;
  isSimpleSuccess?: boolean;
  isOwnerActivation?: boolean;
  onInviteTeam?: () => Promise<void> | void;
  onPrimary?: () => Promise<void> | void;
  onSecondary?: () => Promise<void> | void;
  onDismiss?: () => Promise<void> | void;
  primaryLabel?: string;
  secondaryLabel?: string;
  hintText?: string;
}

export function useFirstEntrySuccessActions() {
  const { t } = useI18n();
  const { navigateToAllStudents } = useRouterHelper();
  const { hasTenantOwnerOrAdminAccess } = usePermissions();
  const { currentTenant, currentTenantOwnersAndAdmins } = useCurrentTenant();
  const { classes, classesOrGroups } = useSchoolClasses();
  const { currentClass, setCurrentClass } = useCurrentClass();
  const { whoAmI } = useWhoAmI();
  const { isActivationActive, onSuccessSecondary, onSuccessDismiss } = useOwnerActivationChannel();

  const resolveNewestNonDemoClass = (): Components.Schemas.SchoolClassDto | undefined => {
    const nonDemoClasses = classes.value.filter((schoolClass) => !schoolClass.isDemoClass);
    return nonDemoClasses
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .at(0);
  };

  const resolveEntryClassOrGroup = (schoolClassId: number): Components.Schemas.SchoolClassDto | null => {
    return (
      (classesOrGroups.value ?? []).find((schoolClass) => schoolClass.id === schoolClassId) ??
      currentClass.value ??
      null
    );
  };

  const getFirstEntrySuccessConfig = (schoolClassId: number): IFirstEntrySuccessConfig => {
    const canInviteTeam = hasTenantOwnerOrAdminAccess.value;

    if (canInviteTeam) {
      return {
        showTeamInvite: false,
        isOwnerActivation: true,
        primaryLabel: t('growth.firstEntrySuccess.secondEntryCta'),
        secondaryLabel: t('growth.firstEntrySuccess.doneCta'),
        onSecondary: isActivationActive.value ? (onSuccessSecondary.value ?? undefined) : undefined,
        onDismiss: isActivationActive.value ? (onSuccessDismiss.value ?? undefined) : undefined,
      };
    }

    const entryClass = resolveEntryClassOrGroup(schoolClassId);
    const isDemoClass = !!entryClass?.isDemoClass;
    const newestNonDemoClass = resolveNewestNonDemoClass();

    if (isDemoClass && newestNonDemoClass) {
      return {
        showTeamInvite: false,
        primaryLabel: t('growth.firstEntrySuccess.goToClassCta'),
        onPrimary: async () => {
          setCurrentClass(newestNonDemoClass);
          await navigateToAllStudents();
        },
      };
    }

    if (isDemoClass && !newestNonDemoClass) {
      const tenant = currentTenant.value;
      const assignmentPath = `/school/${tenant?.id}/settings/classes`;

      const mailto = buildRequestClassMailto({
        recipients: currentTenantOwnersAndAdmins.value,
        sender: whoAmI.value,
        assignmentPath,
        subject: t('growth.firstEntrySuccess.requestClassMailSubject'),
        body: (params) => t('growth.firstEntrySuccess.requestClassMailBody', params),
      });

      if (mailto) {
        return {
          showTeamInvite: false,
          primaryLabel: t('growth.firstEntrySuccess.requestClassCta'),
          secondaryLabel: t('growth.firstEntrySuccess.closeCta'),
          hintText: t('growth.firstEntrySuccess.requestClassHint'),
          onPrimary: () => {
            window.location.href = mailto;
          },
        };
      }
    }

    return { showTeamInvite: false };
  };

  return {
    getFirstEntrySuccessConfig,
  };
}
